import {
  claimPaymentLockOwnership,
  releasePaymentLockOwnership,
} from "@/domain/payment/payment-lock-ownership"

/** Keys used with `sessionStorage` to survive refresh during payment redirect. */
export const PAYMENT_SESSION_ORDER_ID_KEY = "payment_order_id"
export const PAYMENT_SESSION_ATTEMPT_ID_KEY = "payment_attempt_id"
/** Alias storefront base ("/r/{slug}") or "" for legacy — used by same-tab payment return. */
export const PAYMENT_SESSION_STOREFRONT_BASE_KEY = "payment_storefront_base"

export interface PaymentAwaitSession {
  orderId: string
  paymentAttemptId: string
}

/** Session plus the storefront base that owns it (`""` = host-only / legacy). */
export interface OwnedPaymentAwaitSession extends PaymentAwaitSession {
  storefrontBase: string
}

function readOrderId(): string | null {
  try {
    const v = sessionStorage.getItem(PAYMENT_SESSION_ORDER_ID_KEY)
    return v?.trim() ? v.trim() : null
  } catch {
    return null
  }
}

function readAttemptId(): string | null {
  try {
    const v = sessionStorage.getItem(PAYMENT_SESSION_ATTEMPT_ID_KEY)
    return v?.trim() ? v.trim() : null
  } catch {
    return null
  }
}

export function getPaymentSession(): PaymentAwaitSession | null {
  const orderId = readOrderId()
  const paymentAttemptId = readAttemptId()
  if (!orderId || !paymentAttemptId) return null
  return { orderId, paymentAttemptId }
}

export function hasActivePaymentSession(): boolean {
  return getPaymentSession() != null
}

export function readPaymentStorefrontBase(): string {
  const stored = readStoredPaymentStorefrontBase()
  return stored == null ? "" : stored
}

/**
 * Raw stored owner base. `null` means the key was never written (fail closed).
 * `""` is a valid host-only / legacy owner.
 */
export function readStoredPaymentStorefrontBase(): string | null {
  try {
    const v = sessionStorage.getItem(PAYMENT_SESSION_STOREFRONT_BASE_KEY)
    return v == null ? null : v
  } catch {
    return null
  }
}

/**
 * Returns the payment-await session only when it belongs to `currentStorefrontBase`.
 * Missing `payment_storefront_base` fails closed. Does not clear storage.
 */
export function getOwnedPaymentSession(
  currentStorefrontBase: string
): OwnedPaymentAwaitSession | null {
  const session = getPaymentSession()
  if (!session) return null
  const storedBase = readStoredPaymentStorefrontBase()
  if (storedBase == null) return null
  if (storedBase !== currentStorefrontBase) return null
  return {
    orderId: session.orderId,
    paymentAttemptId: session.paymentAttemptId,
    storefrontBase: storedBase,
  }
}

/**
 * Persists payment await markers.
 * Pass `storefrontBasePath` explicitly ("" for legacy). Presentation/utils should resolve
 * the current `/r/{slug}` base — domain must not import routing helpers.
 */
export function persistPaymentSession(
  orderId: string,
  paymentAttemptId: string,
  storefrontBasePath: string = ""
): void {
  try {
    sessionStorage.setItem(PAYMENT_SESSION_ORDER_ID_KEY, String(orderId).trim())
    sessionStorage.setItem(PAYMENT_SESSION_ATTEMPT_ID_KEY, String(paymentAttemptId).trim())
    sessionStorage.setItem(PAYMENT_SESSION_STOREFRONT_BASE_KEY, storefrontBasePath)
  } catch {
    /* quota / private mode */
  }
  claimPaymentLockOwnership(storefrontBasePath)
}

export function clearPaymentSession(): void {
  try {
    sessionStorage.removeItem(PAYMENT_SESSION_ORDER_ID_KEY)
    sessionStorage.removeItem(PAYMENT_SESSION_ATTEMPT_ID_KEY)
    sessionStorage.removeItem(PAYMENT_SESSION_STOREFRONT_BASE_KEY)
  } catch {
    /* ignore */
  }
  releasePaymentLockOwnership()
}
